import { BallotRecordElectionSummary, BallotRecordSummary } from '@/types/ballotRecords';
import { dateLocale } from '@/utils/dateLocale';

/**
 * Pick the election that names a ballot record: the earliest upcoming one,
 * falling back to the most recent past one for history records.
 */
export const pickNamingElection = (
  elections: BallotRecordElectionSummary[]
): BallotRecordElectionSummary | null => {
  const dated = elections.filter((e) => e.election_date);
  if (!dated.length) return null;
  const upcoming = dated
    .filter((e) => !e.is_past)
    .sort((a, b) => a.election_date!.localeCompare(b.election_date!));
  if (upcoming.length) return upcoming[0];
  return dated.sort((a, b) => b.election_date!.localeCompare(a.election_date!))[0];
};

/**
 * Human name for a ballot record, derived from its election date, e.g.
 * "Nov 2026 · General Election". `fallback` covers records with no dated
 * elections. Extra concurrent elections are the caller's concern (see
 * `extraElectionCount`).
 */
export const formatBallotRecordName = (
  record: Pick<BallotRecordSummary, 'elections'>,
  locale: string,
  fallback: string
): string => {
  const election = pickNamingElection(record.elections ?? []);
  if (!election?.election_date) return fallback;
  let monthYear: string;
  try {
    // DW dates are YYYY-MM-DD; parse as local date to avoid TZ off-by-one.
    const [year, month] = election.election_date.split('-').map(Number);
    monthYear = new Intl.DateTimeFormat(dateLocale(locale), {
      month: 'short',
      year: 'numeric',
    }).format(new Date(year, (month ?? 1) - 1, 1));
  } catch {
    monthYear = election.election_date;
  }
  return election.election_name ? `${monthYear} · ${election.election_name}` : monthYear;
};

export const extraElectionCount = (record: Pick<BallotRecordSummary, 'elections'>): number =>
  Math.max((record.elections ?? []).filter((e) => e.election_date).length - 1, 0);
